import { useState, useContext, useEffect, useRef, useCallback } from 'react';

import { Button, Card, Form, Row, Col } from 'react-bootstrap';
import { format } from 'date-fns';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { useRestApi } from 'context/RestApiContext';
import { useMaster } from 'context/MasterContext';

import { MessageModal } from 'components/modal/MessageModal';
import { checkRequired } from 'utils/validationUtils';

import { Chip, Slider } from '@mui/material';
import { ProcessLableComponent, getProcess, PROCESS } from 'components/Input/ProcessLableComponent';
import TextComponent from 'components/Input/TextComponent';
import { isPastYearMonth } from 'utils/dateUtils';

function ClientCardEditModal(props) {
  // マスタ情報
  const master = useMaster();

  const restApi = useRestApi();

  // 確認ダイアログ
  const [dialog, setDialog] = useState({});

  // 顧客情報（修正前の情報）
  const [orgClient, setOrgClient] = useState({});
  // 顧客情報
  const [client, setClient] = useState({});

  // モーダルの表示
  const [show, setShow] = useState(false);

  // ラベル
  const [labels, setLabels] = useState([]);

  // モーダルパラメタの変更処理
  useEffect(() => {
    if (props?.params?.client) {
      restApi.get(`/api/construction/${props?.params?.client.SEIKYU_NO}`, (data) => {
        // 顧客情報
        let _client = {
          ...props?.params?.client,
          KOUJI_MEI: data.construction.KOUJI_MEI,
          PROCESS_CD: data.construction.PROCESS_CD,
          SALES_TAN_CD: data.construction.SALES_TAN_CD,
          DASHBOARD_DISP_DATE: data.construction.DASHBOARD_DISP_DATE,
          LOST_ORDER_DATE: data.construction.LOST_ORDER_DATE,
          UKE_DATE: data.construction.UKE_DATE,
          MEETING_SCHEDULED_DATE: data.construction.MEETING_SCHEDULED_DATE,
          EXPECTED_SALES_MONTH: data.construction.EXPECTED_SALES_MONTH,
          ORDER_LEVEL: data.construction.ORDER_LEVEL,
          CONSTRUCTION_STATUS: data.construction.CONSTRUCTION_STATUS,
          JIGYO_CD: data.construction.JIGYO_CD,
        };
        setClient(_client);
        setOrgClient({ ..._client });
        setLabels(data.construction.LABELS ? data.construction.LABELS.split(',') : []);
        setShow(true);
      });
    }
  }, [props.params]);

  // ラベルの選択処理
  const handleOnClickLabel = (lbl) => {
    if (labels.some((l) => l == lbl)) {
      setLabels(labels.filter((x) => x != lbl));
    } else {
      setLabels([...labels, lbl]);
    }
  };

  // エラーフィールド
  const [errorFileds, setErrorFileds] = useState([]);

  // 入力チェック
  const validate = () => {
    let errors = [];

    checkRequired(client.UKE_DATE, '見積依頼日', 'UKE_DATE', errors);
    if (isEditableLostOrderStatus()) {
      checkRequired(client.LOST_ORDER_DATE, '失注日', 'LOST_ORDER_DATE', errors);
    }
    setErrorFileds(errors.flatMap((x) => x.fields));

    return errors.map((x) => x.msg);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setClient({ ...client, [prop]: target?.value ?? target?.target.value });
    // エラーのクリア
    setErrorFileds([...errorFileds].filter((x) => x != prop));
  };

  const getLostOrderStatusId = () => {
    const status = master.constructionStatus.find((x) => x.name === '失注');
    return status ? status.id : null;
  };
  // 失注ステータスは編集可能
  const isEditableLostOrderStatus = () => {
    const lostOrderId = getLostOrderStatusId();
    if (lostOrderId == null) return false;
    return client.CONSTRUCTION_STATUS == lostOrderId;
  };

  // 閉じるボタン
  const handleClose = () => {
    setShow(false);
  };

  // 確定ボタンが無効かどうか？
  const isDisabledFix = () => {
    // 受注確度が80%以上の場合に売上見込月が未入力
    if (!client.EXPECTED_SALES_MONTH && client.ORDER_LEVEL >= 80) {
      return true;
    }
    return false;
  };

  // 確定ボタン
  const handleFix = () => {
    var error = validate();
    if (error.length > 0) {
      setDialog({
        type: 'error',
        message: error,
      });
      return;
    }
    let today = new Date();
    let dispDate = master.process.find((p) => p.PROCESS_CD == client.PROCESS_CD).DISP_DATE;
    let isChangeProcessCd = orgClient.PROCESS_CD != client.PROCESS_CD; // 工程に変更があったか？
    let dashboardDate = !isChangeProcessCd
      ? orgClient.DASHBOARD_DISP_DATE // 工程が変更なしの場合はそのまま
      : dispDate == 0
      ? '9999-12-31' // 表示期限が無期限の場合
      : format(today.setDate(today.getDate() + dispDate), 'yyyy-MM-dd');

    const params = {
      SEIKYU_NO: client.SEIKYU_NO,
      LABELS: labels,
      PROCESS_CD: client.PROCESS_CD,
      DASHBOARD_DISP_DATE: dashboardDate,
      SALES_TAN_CD: client.SALES_TAN_CD,
      GENBA_TAN_CD: client.GENBA_TAN_CD,
      UKE_DATE: client.UKE_DATE,
      MEETING_SCHEDULED_DATE: client.MEETING_SCHEDULED_DATE,
      EXPECTED_SALES_MONTH: client.EXPECTED_SALES_MONTH,
      LOST_ORDER_DATE: client.LOST_ORDER_DATE,
      ORDER_LEVEL: client.ORDER_LEVEL,
      CONSTRUCTION_STATUS: client.CONSTRUCTION_STATUS,
      JIGYO_CD: client.JIGYO_CD,
      IS_CHANGE_PROCESS: isChangeProcessCd,
    };

    restApi.post('/api/dashboard/label', params, (_) => {
      props.params.callback({
        ...client,
        LABELS: labels,
        DASHBOARD_DISP_DATE: dashboardDate,
        SORT: isChangeProcessCd ? 0 : orgClient.SORT,
      });
      setShow(false);
    });
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '680px', // Set your width here
            },
          },
        }}
        open={show}
        disableEnforceFocus={true} // フォーカス制御を無効にする
      >
        <DialogTitle className="p-3">{client.NAME_1}</DialogTitle>
        <DialogContent className="px-0 py-0 pr-0 pl-0">
          <Card className="m-0">
            <Card.Body>
              <Row>
                <Col className="pl-2" md="12">
                  <Form.Group>
                    <label>工事名</label>
                    <h4 className="m-1">{client.KOUJI_MEI}</h4>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="pl-2 pt-2">
                <Col md="4">
                  <label>見積依頼日</label>
                </Col>
                <Col md="4">
                  <label>打ち合わせ予定日</label>
                </Col>
              </Row>
              <Row className="pt-1">
                <Col md="4">
                  <TextComponent
                    value={client.UKE_DATE}
                    onChange={handleChange('UKE_DATE')}
                    type="date"
                    error={errorFileds.some((x) => x == 'UKE_DATE')}
                  />
                </Col>
                <Col md="4">
                  <TextComponent
                    value={client.MEETING_SCHEDULED_DATE}
                    onChange={handleChange('MEETING_SCHEDULED_DATE')}
                    type="date"
                  />
                </Col>
              </Row>
              <Row className="pl-2 pt-2">
                <Col md="6">
                  <label>売上予定月</label>
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <TextComponent
                    value={client.EXPECTED_SALES_MONTH}
                    onChange={handleChange('EXPECTED_SALES_MONTH')}
                    type="date"
                    selectionMode="Month"
                  />
                </Col>
                <Col className="m-0 p-0 d-flex align-items-end">
                  <div style={{ color: 'red' }}>
                    {!client.EXPECTED_SALES_MONTH && client.ORDER_LEVEL >= 80
                      ? '※受注確度が80%以上は売上予定月を入力してください。'
                      : ''}
                  </div>
                  <div style={{ color: 'orange' }}>
                    {client.EXPECTED_SALES_MONTH && isPastYearMonth(client.EXPECTED_SALES_MONTH)
                      ? '売上年月に過去の年月が設定されています!'
                      : ''}
                  </div>
                </Col>
              </Row>
              <Row className="pl-2 pt-2">
                <label>受注確度</label>
              </Row>
              <Row>
                <Col md="10" className="pl-4">
                  <Slider
                    defaultValue={client.ORDER_LEVEL}
                    valueLabelFormat={(v) => `${v}%`}
                    aria-labelledby="discrete-slider-always"
                    valueLabelDisplay="auto"
                    step={10}
                    min={10}
                    onChange={handleChange('ORDER_LEVEL')}
                    marks={[
                      { value: 10, label: '10%' },
                      { value: 50, label: '50%' },
                      { value: 80, label: '80%' },
                      { value: 100, label: '100%' },
                    ]}
                  />
                </Col>
                <Col style={{ color: '#1976d2' }}>
                  <h4 className="m-0 p-0">{client.ORDER_LEVEL}%</h4>
                </Col>
              </Row>
              <Row>
                <Col className="pl-2" md="5">
                  <Form.Group>
                    <label>ステータス</label>
                  </Form.Group>
                </Col>
                {/** 失注の場合に失注日を表示 */}
                {isEditableLostOrderStatus() && (
                  <Col className="pl-2" md="4">
                    <Form.Group>
                      <label>失注日</label>
                    </Form.Group>
                  </Col>
                )}
              </Row>
              <Row className="pt-1">
                <Col md="5">
                  <Form.Select
                    className="form-control"
                    value={client?.CONSTRUCTION_STATUS}
                    onChange={handleChange('CONSTRUCTION_STATUS')}
                  >
                    {master.constructionStatus.map((x, i) => (
                      <option key={i} value={x['id']}>
                        {x['name']}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                {/** 失注の場合に失注日を表示 */}
                {isEditableLostOrderStatus() && (
                  <Col md="4">
                    <TextComponent
                      type="date"
                      value={client.LOST_ORDER_DATE}
                      onChange={handleChange('LOST_ORDER_DATE')}
                      error={errorFileds.some((x) => x == 'LOST_ORDER_DATE')}
                    />
                  </Col>
                )}
              </Row>
              <Row className="pl-2 pt-2">
                <label>ラベル</label>
              </Row>
              <Row className="pt-1">
                {master.label.map((ml) => (
                  <Chip
                    key={ml.LABEL_CD}
                    className="m-1"
                    style={{
                      width: '112px',
                      color: labels.some((l) => l == ml.LABEL_CD) ? ml.FONT_COLOR : '#172b4d',
                      backgroundColor: labels.some((l) => l == ml.LABEL_CD) ? ml.COLOR : '#e4e4e4',
                    }}
                    label={ml.DISPLAY}
                    onClick={() => handleOnClickLabel(ml.LABEL_CD)}
                  />
                ))}
              </Row>

              <Row className="pl-2 pt-2">
                <Col md="6">
                  <label>営業担当</label>
                </Col>
                <Col md="6">
                  <label>現場担当</label>
                </Col>
              </Row>
              <Row className="pt-1">
                <Col md="6">
                  <Form.Select
                    className="form-control"
                    value={client?.SALES_TAN_CD}
                    onChange={handleChange('SALES_TAN_CD')}
                  >
                    <option value={''}>　</option>
                    {master.salesStaff.map((x, i) => (
                      <option key={i} value={x['id']}>
                        {x['name']}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md="6">
                  <Form.Select
                    className="form-control"
                    value={client?.GENBA_TAN_CD}
                    onChange={handleChange('GENBA_TAN_CD')}
                  >
                    <option value={'0'}>　</option>
                    {master.genbaStaff.map((x, i) => (
                      <option key={i} value={x['id']}>
                        {x['name']}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </DialogContent>
        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <Button variant="secondary" className="footer-button" onClick={handleClose}>
            閉じる
          </Button>
          <Button
            variant="primary btn-fill"
            className="footer-button"
            onClick={handleFix}
            disabled={isDisabledFix()}
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>
      <MessageModal dialog={dialog}></MessageModal>
    </>
  );
}

export default ClientCardEditModal;
